// src/contexts/AlertNotificationContext.tsx
// Context for handling alert notifications via WebSocket with browser push and toast notifications

import React, { createContext, useContext, useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';

export interface AlertNotification {
  type: 'alert_notification';
  event_type: string;
  severity: string;
  title: string;
  message: string;
  action_url?: string;
  project_id?: number;
  scan_id?: number;
  timestamp: string;
  payload?: Record<string, unknown>;
}

export interface ToastNotification {
  id: string;
  severity: string;
  title: string;
  message: string;
  actionUrl?: string;
  timestamp: Date;
}

interface AlertNotificationContextType {
  toasts: ToastNotification[];
  addToast: (notification: Omit<ToastNotification, 'id' | 'timestamp'>) => string;
  removeToast: (id: string) => void;
  requestPermission: () => Promise<NotificationPermission>;
  getPermission: () => NotificationPermission;
  isConnected: boolean;
}

const AlertNotificationContext = createContext<AlertNotificationContextType | null>(null);

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#16a34a',
  info: '#3b82f6',
};

const getSeverityIcon = (severity: string): string => {
  switch (severity) {
    case 'critical':
      return '\u{1F6A8}'; // 🚨
    case 'high':
      return '\u{26A0}\u{FE0F}'; // ⚠️
    case 'medium':
      return '\u{1F4E2}'; // 📢
    case 'low':
      return '\u{1F4DD}'; // 📝
    default:
      return '\u{1F514}'; // 🔔
  }
};

export const AlertNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { socket, isConnected } = useWebSocket();
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const toastIdCounter = useRef(0);

  // Add a toast notification
  const addToast = useCallback((notification: Omit<ToastNotification, 'id' | 'timestamp'>) => {
    const id = `toast-${++toastIdCounter.current}`;
    const newToast: ToastNotification = {
      ...notification,
      id,
      timestamp: new Date(),
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-remove toast after 6 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 6000);

    return id;
  }, []);

  // Remove a specific toast
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Debounce tracking for agent connect/disconnect notifications
  const agentAlertTimestamps = useRef<Record<string, number>>({});

  // Handle incoming alert notification
  const handleAlert = useCallback((data: AlertNotification) => {
    if (data.type !== 'alert_notification') return;

    console.log('[ALERT] Received alert notification:', data.event_type, data.title);

    // Debounce agent online/offline notifications — suppress ANY agent status change
    // within 60s per agent (keyed by agent name from title, not event type).
    // This prevents offline→online→offline spam during network hiccups.
    if (data.event_type === 'agent_online' || data.event_type === 'agent_offline') {
      // Extract agent identity from title (e.g., "Agent Online: MyAgent" → "agent:MyAgent")
      const agentName = data.title?.replace(/^Agent (Online|Offline): /, '') || data.title;
      const dedupeKey = `agent_status:${agentName}`;
      const now = Date.now();
      const lastShown = agentAlertTimestamps.current[dedupeKey] || 0;
      if (now - lastShown < 60000) {
        console.log('[ALERT] Suppressing agent status notification:', dedupeKey, `(${now - lastShown}ms since last)`);
        return;
      }
      agentAlertTimestamps.current[dedupeKey] = now;
    }

    // Check if browser notifications are enabled for this event type in localStorage
    const browserEnabled = localStorage.getItem(`alert_browser_${data.event_type}`) !== 'false';

    // Always show in-app toast
    addToast({
      severity: data.severity,
      title: data.title,
      message: data.message,
      actionUrl: data.action_url,
    });

    // Show browser notification if enabled and permission granted
    if (browserEnabled && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const notification = new Notification(data.title, {
          body: data.message,
          icon: '/favicon.ico',
          tag: `alert-${data.event_type}-${Date.now()}`,
          requireInteraction: data.severity === 'critical',
        });

        // Handle click on notification
        notification.onclick = () => {
          window.focus();
          if (data.action_url) {
            window.location.href = data.action_url;
          }
          notification.close();
        };

        // Auto-close after 10 seconds (except critical)
        if (data.severity !== 'critical') {
          setTimeout(() => notification.close(), 10000);
        }
      } catch (err) {
        console.error('[ALERT] Failed to show browser notification:', err);
      }
    }
  }, [addToast]);

  // Listen for WebSocket messages
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'alert_notification') {
          handleAlert(data as AlertNotification);
        }
      } catch (err) {
        // Not a JSON message or not an alert
      }
    };

    socket.addEventListener('message', handleMessage);

    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket, handleAlert]);

  // Request browser notification permission
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    const permission = await Notification.requestPermission();
    return permission;
  }, []);

  // Get current browser notification permission
  const getPermission = useCallback((): NotificationPermission => {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }, []);

  const value = useMemo(() => ({
    toasts,
    addToast,
    removeToast,
    requestPermission,
    getPermission,
    isConnected,
  }), [toasts, addToast, removeToast, requestPermission, getPermission, isConnected]);

  return (
    <AlertNotificationContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </AlertNotificationContext.Provider>
  );
};

// Toast container component
const ToastContainer: React.FC<{
  toasts: ToastNotification[];
  removeToast: (id: string) => void;
}> = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '1rem',
      right: '1rem',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      maxWidth: '400px',
    }}>
      {toasts.map(toast => (
        <div
          key={toast.id}
          style={{
            background: 'var(--bg-primary, #1e293b)',
            border: `3px solid ${SEVERITY_COLORS[toast.severity] || SEVERITY_COLORS.info}`,
            borderRadius: '8px',
            padding: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
            animation: 'alertSlideIn 0.3s ease-out',
            cursor: toast.actionUrl ? 'pointer' : 'default',
          }}
          onClick={() => {
            if (toast.actionUrl) {
              window.location.href = toast.actionUrl;
            }
            removeToast(toast.id);
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem' }}>{getSeverityIcon(toast.severity)}</span>
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 600,
                color: 'var(--text-primary, #f1f5f9)',
                marginBottom: '0.25rem',
                fontSize: '0.9rem',
              }}>
                {toast.title}
              </div>
              <div style={{
                color: 'var(--text-secondary, #94a3b8)',
                fontSize: '0.85rem',
                lineHeight: 1.4,
              }}>
                {toast.message.split('\n').map((line, i) => (
                  <div key={i} style={i > 0 ? {
                    marginTop: '0.25rem',
                    fontSize: '0.75rem',
                    color: 'var(--text-tertiary, #64748b)',
                    fontStyle: 'italic',
                  } : undefined}>
                    {line}
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeToast(toast.id);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary, #94a3b8)',
                cursor: 'pointer',
                padding: '0.25rem',
                fontSize: '1rem',
                lineHeight: 1,
              }}
            >
              x
            </button>
          </div>
        </div>
      ))}
      <style>{`
        @keyframes alertSlideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

// Hook to use alert notifications
export const useAlertNotifications = () => {
  const context = useContext(AlertNotificationContext);
  if (!context) {
    throw new Error('useAlertNotifications must be used within AlertNotificationProvider');
  }
  return context;
};

export default AlertNotificationProvider;
